import React, {Component} from "react";
import '../modals/AddDevice/AddDeviceModal.scss';
import {Button, Dropdown, Input, StateOptions} from "gd-react";

/**
 * BACnet/IP connection settings, used both when adding a device (DeviceForm)
 * and when editing one (AssetSettingsDetails).
 *
 * Owns its own state. The parent renders it via a ref and calls:
 *  - validate()   -> boolean, flags errors when invalid
 *  - getDetails() -> the object for the asset's bacnet_tcp_ip_details
 *
 * Props:
 *  - asset          asset/device-type object (its sku drives the data types)
 *  - initialDetails existing bacnet_tcp_ip_details to pre-fill (edit mode)
 *  - onChange       called whenever the user edits a field
 *  - disabled       render the form read-only
 *  - hideHeader     skip the internal divider + title (when a card header exists)
 *
 * Field names are camelCase to match the bacnet_tcp_ip_details schema.
 */
class BacnetDeviceDetails extends Component {

    constructor(props) {
        super(props);
        this.errors = {};
        this.state = {
            triedToSave: false,
            bacnetDetails: this.normaliseInitial(props.initialDetails)
        };
        ['getDataTypeOptions', 'defaultProperty', 'handleChange', 'handlePropertyChange',
            'addProperty', 'removeProperty', 'notifyChange', 'checkErrors', 'validate',
            'getDetails'].forEach((func) => this[func] = this[func].bind(this));
    }

    normaliseInitial(initial) {
        if (!initial) {
            return {host: '', broadcastAddress: '', port: 47808, timeout: 5000, properties: []};
        }
        return {
            host: initial.host || '',
            broadcastAddress: initial.broadcastAddress || '',
            port: initial.port ?? 47808,
            timeout: initial.timeout ?? 5000,
            properties: (initial.properties || []).map((p) => ({
                dataType: p.dataType || '',
                instance: p.instance ?? '',
                multiplier: p.multiplier ?? 1
            }))
        };
    }

    componentDidMount() {
        if (!this.state.bacnetDetails.properties.length) {
            let bacnetDetails = this.state.bacnetDetails;
            bacnetDetails.properties.push(this.defaultProperty());
            this.setState({bacnetDetails: bacnetDetails});
        }
    }

    getDataTypeOptions() {
        let stateOption = StateOptions.find((so) => so.type === (this.props.asset && this.props.asset.sku));
        let dataTypes = (stateOption && stateOption.dataTypes) || [];
        return dataTypes
            .filter(Boolean)
            .map((dt) => ({value: dt.type, title: dt.label || dt.type}));
    }

    defaultProperty() {
        let dataTypeOptions = this.getDataTypeOptions();
        return {
            dataType: dataTypeOptions.length === 1 ? dataTypeOptions[0].value : '',
            instance: '',
            multiplier: 1
        };
    }

    notifyChange() {
        if (this.props.onChange) this.props.onChange();
    }

    handleChange(input, field) {
        let bacnetDetails = this.state.bacnetDetails;
        bacnetDetails[field] = input;
        this.setState({bacnetDetails: bacnetDetails, triedToSave: false});
        this.notifyChange();
    }

    handlePropertyChange(input, index, field) {
        let bacnetDetails = this.state.bacnetDetails;
        bacnetDetails.properties[index][field] = input;
        this.setState({bacnetDetails: bacnetDetails, triedToSave: false});
        this.notifyChange();
    }

    addProperty() {
        let bacnetDetails = this.state.bacnetDetails;
        bacnetDetails.properties.push(this.defaultProperty());
        this.setState({bacnetDetails: bacnetDetails, triedToSave: false});
        this.notifyChange();
    }

    removeProperty(index) {
        let bacnetDetails = this.state.bacnetDetails;
        bacnetDetails.properties.splice(index, 1);
        this.setState({bacnetDetails: bacnetDetails, triedToSave: false});
        this.notifyChange();
    }

    checkErrors() {
        let errors = {};
        let bd = this.state.bacnetDetails;
        let isNotNumber = (val) => val === '' || val === null || val === undefined || isNaN(Number(val));
        if (!bd.host) errors['host'] = true;
        if (!bd.broadcastAddress) errors['broadcastAddress'] = true;
        if (isNotNumber(bd.port)) errors['port'] = true;
        if (isNotNumber(bd.timeout)) errors['timeout'] = true;
        if (!bd.properties.length) errors['properties'] = true;
        bd.properties.forEach((p, i) => {
            if (!p.dataType) errors[`propertyDataType${i}`] = true;
            if (isNotNumber(p.instance)) errors[`propertyInstance${i}`] = true;
            if (isNotNumber(p.multiplier)) errors[`propertyMultiplier${i}`] = true;
        });
        this.errors = errors;
    }

    // Called by the parent (via ref) before saving. Returns true when valid.
    validate() {
        this.checkErrors();
        this.setState({triedToSave: true});
        return Object.keys(this.errors).length === 0;
    }

    // Called by the parent (via ref) to read the value for the asset.
    getDetails() {
        let bd = this.state.bacnetDetails;
        return {
            host: bd.host,
            port: parseInt(bd.port),
            timeout: parseInt(bd.timeout),
            broadcastAddress: bd.broadcastAddress,
            properties: bd.properties.map((p) => ({
                dataType: p.dataType,
                instance: parseInt(p.instance),
                multiplier: Number(p.multiplier)
            }))
        };
    }

    render() {
        this.checkErrors();
        let self = this;
        let bd = this.state.bacnetDetails;
        let triedToSave = this.state.triedToSave;
        let disabled = this.props.disabled;
        let dataTypeOptions = this.getDataTypeOptions();
        return (
            <div>
                {!this.props.hideHeader ?
                    <div>
                        <div className={'add-device-divider'}/>
                        <div className={'row title-wrapper'} style={{marginBottom: '-10px'}}>
                            <div className={'text-wrapper'}>
                                <p>BACnet/IP Connection</p>
                            </div>
                        </div>
                    </div> : null}
                <div className={'row'} style={{marginTop: '14px'}}>
                    <Input
                        label={'Host IP Address'}
                        name={'bacnet-host'}
                        disabled={disabled}
                        error={triedToSave && this.errors['host']}
                        value={bd.host}
                        placeholder={'e.g. 192.168.36.10'}
                        onChange={(input) => self.handleChange(input.target.value, 'host')}
                    />
                </div>
                <div className={'row'} style={{marginTop: '14px'}}>
                    <Input
                        label={'Broadcast Address'}
                        name={'bacnet-broadcast-address'}
                        disabled={disabled}
                        error={triedToSave && this.errors['broadcastAddress']}
                        value={bd.broadcastAddress}
                        placeholder={'e.g. 10.35.129.255'}
                        onChange={(input) => self.handleChange(input.target.value, 'broadcastAddress')}
                    />
                </div>
                <div className={'row'} style={{marginTop: '14px'}}>
                    <Input
                        label={'Port'}
                        name={'bacnet-port'}
                        type={'number'}
                        disabled={disabled}
                        error={triedToSave && this.errors['port']}
                        value={bd.port}
                        placeholder={'e.g. 47808'}
                        onChange={(input) => self.handleChange(input.target.value, 'port')}
                    />
                </div>
                <div className={'row'} style={{marginTop: '14px'}}>
                    <Input
                        label={'Timeout (ms)'}
                        name={'bacnet-timeout'}
                        type={'number'}
                        disabled={disabled}
                        error={triedToSave && this.errors['timeout']}
                        value={bd.timeout}
                        placeholder={'e.g. 5000'}
                        onChange={(input) => self.handleChange(input.target.value, 'timeout')}
                    />
                </div>
                {bd.properties.map((property, index) => (
                    <div key={`bacnet-property-${index}`}>
                        <div className={'add-device-divider'}/>
                        <div className={'row'} style={{justifyContent: 'space-between', alignItems: 'center'}}>
                            <p style={{fontWeight: 'bold', margin: 0}}>Property {index + 1}</p>
                            {!disabled && bd.properties.length > 1 ?
                                <p style={{color: '#fc6663', cursor: 'pointer', margin: 0, fontSize: '13px'}}
                                   onClick={() => self.removeProperty(index)}>Remove</p> : null}
                        </div>
                        <div className={'row'} style={{marginTop: '14px'}}>
                            <Dropdown
                                label={'Data Type'}
                                name={`bacnet-property-data-type-${index}`}
                                disabled={disabled}
                                error={triedToSave && this.errors[`propertyDataType${index}`]}
                                value={property.dataType}
                                fixeditems={dataTypeOptions}
                                placeholder={'Select a data type'}
                                onChange={(input) => self.handlePropertyChange(input.target.value, index, 'dataType')}
                            />
                        </div>
                        <div className={'row'} style={{marginTop: '14px'}}>
                            <Input
                                label={'Object Instance'}
                                name={`bacnet-property-instance-${index}`}
                                type={'number'}
                                disabled={disabled}
                                error={triedToSave && this.errors[`propertyInstance${index}`]}
                                value={property.instance}
                                placeholder={'e.g. 58'}
                                onChange={(input) => self.handlePropertyChange(input.target.value, index, 'instance')}
                            />
                        </div>
                        <div className={'row'} style={{marginTop: '14px'}}>
                            <Input
                                label={'Multiplier'}
                                name={`bacnet-property-multiplier-${index}`}
                                type={'number'}
                                disabled={disabled}
                                error={triedToSave && this.errors[`propertyMultiplier${index}`]}
                                value={property.multiplier}
                                placeholder={'e.g. 100'}
                                onChange={(input) => self.handlePropertyChange(input.target.value, index, 'multiplier')}
                            />
                        </div>
                    </div>
                ))}
                {!disabled ?
                    <div className={'row'} style={{marginTop: '20px', height: '50px'}}>
                        <Button
                            label={'+ Add Property'}
                            onClick={() => self.addProperty()}
                        />
                    </div> : null}
            </div>
        );
    }
}

export default BacnetDeviceDetails;
